import { useCallback, useEffect, useState } from 'react'
import { LayoutGrid } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Modal } from '@/components/ui/Modal'
import { BlocoComposer } from '@/components/Blocos/BlocoComposer'
import { BlocoKanbanColumnsPanel } from '@/components/Blocos/BlocoKanbanColumnsPanel'
import { BlocoLabelPicker } from '@/components/Blocos/BlocoLabelPicker'
import { BlocoThreadModal } from '@/components/Blocos/BlocoThreadModal'
import { TarefasKanbanBoard } from '@/components/Blocos/TarefasKanbanBoard'
import { getInitialKanbanColumnId } from '@/components/Blocos/blocosKanbanUtils'
import { useAuthStore } from '@/stores/authStore'
import { useBlocos } from '@/hooks/useBlocos'

export default function Tarefas() {
  const currentUserId = useAuthStore((s) => s.user?.id)
  const blocos = useBlocos()
  const [composerOpen, setComposerOpen] = useState(false)
  const [labelFilterId, setLabelFilterId] = useState<string | null>(null)

  useEffect(() => {
    void blocos.loadBlocosPageData()
  }, [blocos.loadBlocosPageData])

  const handleMoveColumn = useCallback(
    (blocoId: string, columnId: string) => {
      void blocos.moveBlocoToColumn(blocoId, columnId)
    },
    [blocos],
  )

  const handleOpenThread = useCallback(
    (id: string) => {
      void blocos.loadBlocoThread(id)
    },
    [blocos],
  )

  const toggleLabelFilter = useCallback((labelId: string) => {
    setLabelFilterId((prev) => (prev === labelId ? null : labelId))
  }, [])

  const initialColumnId = getInitialKanbanColumnId(blocos.kanbanColumns)

  return (
    <DashboardLayout title='Tarefas'>
      <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-2 text-sm text-text-muted'>
          <LayoutGrid size={16} />
          <span>Quadro Kanban dos seus blocos</span>
        </div>
        <button
          type='button'
          onClick={() => setComposerOpen(true)}
          className='rounded-lg bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90'
        >
          Nova tarefa
        </button>
      </div>

      <div className='mb-4 space-y-3'>
        <BlocoKanbanColumnsPanel
          columns={blocos.kanbanColumns}
          loading={blocos.columnsLoading}
          onCreate={blocos.addKanbanColumn}
          onRename={blocos.renameKanbanColumn}
          onRemove={blocos.removeKanbanColumn}
          onReorder={blocos.reorderKanbanColumns}
        />

        <div className='rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] p-3'>
          <p className='mb-2 text-xs font-medium text-text-muted'>Filtrar por etiqueta</p>
          <BlocoLabelPicker
            labels={blocos.labels}
            selectedIds={labelFilterId ? [labelFilterId] : []}
            saving={blocos.labelsLoading}
            onToggle={toggleLabelFilter}
            onCreate={blocos.addLabel}
            onDelete={blocos.removeLabel}
          />
        </div>
      </div>

      <TarefasKanbanBoard
        blocos={blocos.items}
        columns={blocos.kanbanColumns}
        loading={blocos.loading || blocos.columnsLoading}
        currentUserId={currentUserId}
        labelFilterId={labelFilterId}
        onMoveColumn={handleMoveColumn}
        onOpenBloco={handleOpenThread}
      />

      {composerOpen && (
        <Modal open={composerOpen} onClose={() => setComposerOpen(false)} title='Nova tarefa' size='2xl'>
          <BlocoComposer
            saving={blocos.saving}
            currentUserId={currentUserId}
            onSubmit={async (payload, files) => {
              await blocos.addBloco(
                {
                  ...payload,
                  ...(initialColumnId ? { kanbanColumnId: initialColumnId } : {}),
                },
                files,
              )
              setComposerOpen(false)
            }}
          />
        </Modal>
      )}

      <BlocoThreadModal
        open={Boolean(blocos.threadDetail)}
        detail={blocos.threadDetail}
        loading={blocos.threadLoading}
        saving={blocos.threadSaving}
        currentUserId={currentUserId}
        labels={blocos.labels}
        onClose={blocos.clearThread}
        onComplete={(id) => void blocos.completeBloco(id)}
        onDelete={(id) => void blocos.removeBloco(id)}
        onSendMessage={(id, body) => blocos.sendThreadMessage(id, { body })}
        onSaveMembers={(id, memberUserIds) => blocos.saveThreadMembers(id, { memberUserIds })}
        onUploadAttachment={(id, file) => blocos.uploadThreadAttachment(id, file)}
        onAssignLabels={(id, labelIds) => blocos.assignBlocoLabels(id, labelIds)}
        onCreateLabel={blocos.addLabel}
      />
    </DashboardLayout>
  )
}
